import { Container, ButtonV2, ButtonV2Props } from 'components/index';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './OtherProductsSection.module.css';

type ProductCardType = {
  id: string;
  title: string;
  description: string;
  image: string;
  textColor?: string;
  styling?: {
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
    descriptionColor?: string;
  };
  cta?: ButtonV2Props;
  ctaAlt?: ButtonV2Props;
};

const v1BasePath = 'web_v2/';

const ProductItem = ({
  borderColor,
  backgroundImage,
  children,
}: {
  borderColor?: string;
  backgroundImage?: string;
  children: React.ReactNode;
}) => (
  <div
    className={styles.productItem}
    style={{
      boxShadow: borderColor ? `inset 0 0 0 1px ${borderColor}` : 'none',
      backgroundImage: `url(${backgroundImage})`,
    }}
  >
    {children}
  </div>
);

const ProductContent = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.productContent}>{children}</div>
);

const ProductTitle = ({
  textColor,
  children,
}: {
  textColor?: string;
  children: React.ReactNode;
}) => (
  <h3
    className={styles.productTitle}
    style={{ '--pt-color': textColor } as React.CSSProperties}
  >
    {children}
  </h3>
);

const ProductDescription = ({
  textColor,
  descriptionColor,
  children,
}: {
  textColor?: string;
  descriptionColor?: string;
  children: React.ReactNode;
}) => (
  <p
    className={styles.productDescription}
    style={{ '--pd-color': descriptionColor || textColor } as React.CSSProperties}
  >
    {children}
  </p>
);

const ProductActions = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.productActions}>{children}</div>
);

export const OtherProductSection = ({
  className,
  title,
  subtitle,
}: {
  className?: string;
  title: string;
  subtitle: string;
}) => {
  // Define product cards based on region
  const productCards: ProductCardType[] = [
    // Blood Vision
    {
      id: 'blood-vision',
      title: 'Blood Vision',
      description: 'Advanced blood testing of 100+ biomarkers.',
      textColor: '#ffffff',
      styling: {
        descriptionColor: '#ffffff',
      },
      image: `${v1BasePath}ring-buy/other-products-section/bv-product-thumb.png`,
      cta: {
        variant: 'white' as const,
        label: 'Book test',
        href: '/blood-vision/buy',
        'data-buttontype': 'bloodvision_features_products_buy_button',
      },
    },
    // Home Monitor
    {
      id: 'home-monitor',
      title: 'Home Monitor',
      description:
        'A quiet yet powerful sleep monitor to help you rest better.',
      textColor: '#000000',
      styling: {
        descriptionColor: '#000000',
        borderColor: 'rgba(0,0,0,0.1)',
      },
      image: `${v1BasePath}ring-buy/other-products-section/home-product-thumb-2.png`,
      cta: {
        variant: 'blue' as const,
        label: 'Buy now',
        href: '/home/buy/',
        'data-buttontype': 'home_features_products_buy_button',
      },
    },
    // M1
    // {
    //   id: 'm1',
    //   title: 'Track Glucose with M1',
    //   description:
    //     'Optimize your metabolic fitness with continuous glucose tracking and real-time actionable insights.',
    //   textColor: '#ffffff',
    //   styling: {
    //     descriptionColor: '#ffffff',
    //   },
    //   image: `${v1BasePath}ring-buy/other-products-section/m1-product-thumb.png`,
    //   cta: {
    //     variant: 'blue' as const,
    //     label: 'Buy now',
    //     href: '/pricing/',
    //     'data-buttontype': 'm1_features_products_buy_button',
    //   },
    // },
  ];

  return (
    <section
      className={className ? `${styles.root} ${className}` : styles.root}
    >
      <Container className={styles.container}>
        <h2>{title}</h2>
        <p>{subtitle}</p>
        <div className="cards-container">
          {productCards.map((item) => (
            <ProductItem
              key={item.id}
              borderColor={item.styling?.borderColor}
              backgroundImage={getCompressedAssetUrl(
                item.image?.startsWith('/') ? item.image : `/${item.image}`,
              )}
            >
              <ProductContent>
                <ProductTitle
                  textColor={item.textColor || item.styling?.textColor}
                >
                  {item.title}
                </ProductTitle>
                <ProductDescription
                  textColor={item.textColor}
                  descriptionColor={item.styling?.descriptionColor}
                >
                  {item.description}
                </ProductDescription>
                {(item.cta || item.ctaAlt) && (
                  <ProductActions>
                    {item.cta && (
                      <ButtonV2 variant="blue" {...item.cta}>
                        {item.cta.label}
                      </ButtonV2>
                    )}
                    {item.ctaAlt && (
                      <ButtonV2 variant="transparentLight" {...item.ctaAlt}>
                        {item.ctaAlt.label}
                      </ButtonV2>
                    )}
                  </ProductActions>
                )}
              </ProductContent>
            </ProductItem>
          ))}
        </div>
      </Container>
    </section>
  );
};
